import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Area, AreaChart, Bar, BarChart, Cell, ResponsiveContainer } from "recharts";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { LinkedPanel } from "../../../components/PortfolioPerformance/linked-panel";
import { Skeleton } from "../../../ui/skeleton";
import { TIER_COLOR } from "../../../components/PortfolioPerformance/macro-tier-colors";
import {
  EpdContractsPreview,
  MacroBorrowerPreview,
  SmIndustryPreview,
} from "../../../components/PortfolioPerformance/watchlist-tables";
import { useEpdRate } from "../lib/api/epd";
import { useWatchlist } from "../lib/api/watchlist";

function Sparkline({ data, color }: { data: { index: number; value: number }[]; color: string }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          fill={color}
          fillOpacity={0.15}
          isAnimationActive={false}
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function TierBars({ data }: { data: { tier: string; count: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <Bar dataKey="count" isAnimationActive={false}>
          {data.map((d) => (
            <Cell
              key={d.tier}
              fill={TIER_COLOR[d.tier as keyof typeof TIER_COLOR] ?? "var(--neutral-inactive)"}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Shown in place of EPD/Macro Exposure when their backend field is null --
 *  not yet wired to a real table (epd_contract/macro_sensitivity), so this
 *  shows an honest "not available" state rather than a fabricated stat.
 *  Still links through to the sub-page, which has its own (separate,
 *  unaffected) content. Same outer card styling as LinkedPanel/the Macro
 *  Exposure card below, so it sits in the grid without looking out of place. */
function WatchlistCardPlaceholder({
  to,
  title,
  description,
}: {
  to: string;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">{title}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      <div className="mt-4 flex flex-1 items-center justify-center py-6">
        <p className="text-xs text-muted-foreground">Not available yet</p>
      </div>
    </Link>
  );
}

export default function Watchlist() {
  const { data, isPending, isError, error } = useWatchlist();
  // The Early Payment Defaults card shows the EPD exposure in dollars and, under it, the EPD rate (% of bookings, the EPD page's own
  // card). The rate is its own request (its denominator is a heavy portfolio_performance lookup), so the card never waits on it: a
  // dash-free "…" while it loads, and nothing at all if there is no rate to show.
  const epdRate = useEpdRate();
  const epdRatePct = epdRate.data?.ratePctOfBookings;
  const epdRateText = epdRate.isPending ? "…" : epdRatePct && epdRatePct !== "n/a" ? `${epdRatePct} of bookings` : null;

  return (
    <div>
      <PageHeader
        title="Watchlist"
        subtitle="What needs eyes on it right now, before it becomes a loss: special mention, early payment defaults, and macro-driven industry sensitivity."
        asOfPage="watchlist"
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 px-4 py-3 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load watchlist data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending || !data ? (
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : (
        // Three cards in a row with their table directly under each. The DOM order is cards first, then tables, so on a wide
        // screen they form two aligned rows; below `lg` the columns collapse to one and `order-*` interleaves them again
        // so every table still follows its own card.
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="order-1 grid lg:order-none">
          <LinkedPanel
            to="/portfolio-performance/apd-monitoring"
            title="Special Mention"
            description={data.specialMention.thresholdPct != null ? `APD ≥ ${data.specialMention.thresholdPct}%` : undefined}
            statLabel="Special Mention and Worse Exposure"
            stat={data.specialMention.exposure}
            trend={{
              ...data.specialMention.trend,
              // the same wording as the other cards' comparisons, with the month in brackets: "22.0% vs previous month (Jul 2026)"
              value: data.specialMention.trendVs
                ? `${data.specialMention.trend.value} vs previous month (${data.specialMention.trendVs})`
                : data.specialMention.trend.value,
            }}
            goodDirection="down"
            chart={<Sparkline data={data.specialMention.sparkline} color="var(--decline)" />}
            footnote={
              data.specialMention.fileDate
                ? `on ${format(new Date(`${data.specialMention.fileDate}T00:00:00`), "d MMM yyyy")}`
                : undefined
            }
          />
          </div>
          <div className="order-3 grid lg:order-none">
          {data.epd ? (
            <LinkedPanel
              to="/portfolio-performance/epd"
              title="Early Payment Defaults"
              description="Distress within 6 months of origination"
              statLabel="EPD Contracts (TTM)"
              stat={String(data.epd.contracts)}
              detail={`${data.epd.exposure} EPD exposure`}
              trend={epdRateText ? { value: epdRateText, direction: "flat" } : undefined}
              chart={<Sparkline data={data.epd.sparkline} color="var(--amber)" />}
              footnote="A different diagnostic from APD: EPD flags origination quality, not portfolio seasoning."
            />
          ) : (
            <WatchlistCardPlaceholder
              to="/portfolio-performance/epd"
              title="Early Payment Defaults"
              description="Distress within 6 months of origination"
            />
          )}
          </div>

          <div className="order-5 grid lg:order-none">
          {data.macroExposure ? (
            <Link
              to="/portfolio-performance/macro-exposure"
              className="flex flex-col border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            >
              <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                Macro Exposure
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Industries ranked by credit-risk sensitivity
              </p>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Asset Industries: High
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                    {data.macroExposure.asset.high}{" "}
                    <span className="text-sm text-muted-foreground">
                      of {data.macroExposure.asset.total}
                    </span>
                  </p>
                  <div className="mt-2 h-12 w-full">
                    <TierBars data={data.macroExposure.asset.tierCounts} />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Borrower Industries: High
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                    {data.macroExposure.borrower.high}{" "}
                    <span className="text-sm text-muted-foreground">
                      of {data.macroExposure.borrower.total}
                    </span>
                  </p>
                  <div className="mt-2 h-12 w-full">
                    <TierBars data={data.macroExposure.borrower.tierCounts} />
                  </div>
                </div>
              </div>

              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                High concentration + high macro sensitivity is the signal worth acting on.
              </p>
            </Link>
          ) : (
            <WatchlistCardPlaceholder
              to="/portfolio-performance/macro-exposure"
              title="Macro Exposure"
              description="Industries ranked by credit-risk sensitivity"
            />
          )}
          </div>

          <div className="order-2 grid lg:order-none">
            <SmIndustryPreview />
          </div>
          <div className="order-4 grid lg:order-none">
            <EpdContractsPreview />
          </div>
          <div className="order-6 grid lg:order-none">
            <MacroBorrowerPreview />
          </div>
        </div>
      )}
    </div>
  );
}
